import {
  ACTIVE_CANDIDATE_SLUGS,
  displayName,
  type Slug,
} from "../../lib/candidates.ts";
import {
  AVATAR_KINDS,
  avatarImage,
  type AvatarKind,
  candidateImage,
} from "../assets.ts";

export interface BadgeChoice {
  kind: AvatarKind;
  votingFor: Slug;
}

const KIND_LABEL: Record<AvatarKind, string> = { male: "Él", female: "Ella" };

// Shuffle a copy (Fisher–Yates) — candidate order is randomised each load so
// the layout never favours one candidate over another.
const shuffle = <T>(xs: readonly T[]): T[] => {
  const a = [...xs];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
};

// Entry modal over the live party: pick an avatar first, then a candidate
// (small images) — picking a candidate completes the phrase
// "ellos no saben que voto por ___". Calls onDone once.
export function showBadgePicker(onDone: (choice: BadgeChoice) => void): void {
  let kind: AvatarKind | null = null;
  let votingFor: Slug | null = null;
  const candidateOrder = shuffle(ACTIVE_CANDIDATE_SLUGS); // randomised per load

  const overlay = document.createElement("div");
  overlay.className =
    "fixed inset-0 z-30 flex items-center justify-center bg-black/30 p-4";
  // Fixed-height column: title + enter button pinned (always visible), only the
  // selection area in the middle scrolls if it doesn't fit.
  const card = document.createElement("div");
  card.className =
    "flex max-h-[90vh] w-full max-w-2xl flex-col rounded-2xl border-2 border-black bg-white/95 p-5 text-center shadow-2xl";

  // The phrase — completes with the chosen candidate's name (underlined).
  const title = document.createElement("h1");
  title.className = "shrink-0 pt-2 text-2xl font-bold";
  const renderTitle = () => {
    if (votingFor) {
      title.innerHTML =
        `ellos no saben que voto por <span class="underline decoration-2 underline-offset-2">${
          displayName(votingFor)
        }</span>`;
    } else {
      title.textContent = "ellos no saben que voto por ___";
    }
  };
  renderTitle();

  const enter = document.createElement("button");
  enter.className =
    "mt-3 shrink-0 self-center rounded-xl border-2 border-black px-6 py-2 text-lg font-bold disabled:opacity-30";
  enter.textContent = "Entrar a La Fiesta de la democracia →";
  enter.disabled = true;
  // Activated once a candidate is chosen (avatar is asked first, so both set).
  const refresh = () => {
    enter.disabled = votingFor === null || kind === null;
  };

  // A selectable image tile. `caption` shows the label under the image (avatars);
  // candidates pass false → image only, name in `alt` for accessibility.
  const tile = (
    src: string,
    label: string,
    selected: boolean,
    imgClass: string,
    caption: boolean,
  ): HTMLButtonElement => {
    const b = document.createElement("button");
    b.className =
      "flex flex-col items-center gap-1 rounded-xl bg-white p-1.5 " +
      (selected
        ? "outline outline-4 outline-black"
        : "outline outline-1 outline-neutral-300");
    const img = document.createElement("img");
    img.src = src;
    img.alt = label;
    img.className = `${imgClass} object-contain`;
    b.appendChild(img);
    if (caption) {
      const cap = document.createElement("span");
      cap.className = "text-xs";
      cap.textContent = label;
      b.appendChild(cap);
    }
    return b;
  };

  // 1) Avatar (asked first).
  const avatarHead = document.createElement("h2");
  avatarHead.className = "font-bold";
  avatarHead.textContent = "Elige tu avatar";
  const avatarRow = document.createElement("div");
  avatarRow.className = "flex flex-wrap justify-center gap-3";
  const renderAvatars = () => {
    avatarRow.replaceChildren();
    for (const k of AVATAR_KINDS) {
      const b = tile(
        avatarImage(k),
        KIND_LABEL[k],
        k === kind,
        "h-20 w-20",
        true,
      );
      b.addEventListener("click", () => {
        kind = k;
        renderAvatars();
        refresh();
      });
      avatarRow.appendChild(b);
    }
  };
  renderAvatars();

  // 2) Candidate (below, small images, NO names) — completes the phrase.
  // Mobile: a 3-column grid → always two rows (3 + 2). Desktop: one flex row.
  const candRow = document.createElement("div");
  candRow.className =
    "grid grid-cols-3 justify-items-center gap-2 sm:flex sm:flex-wrap sm:justify-center";
  const renderCandidates = () => {
    candRow.replaceChildren();
    for (const slug of candidateOrder) {
      const b = tile(
        candidateImage(slug),
        displayName(slug),
        slug === votingFor,
        "h-16 w-16",
        false,
      );
      b.addEventListener("click", () => {
        votingFor = slug;
        renderCandidates();
        renderTitle();
        refresh();
      });
      candRow.appendChild(b);
    }
  };
  renderCandidates();

  enter.addEventListener("click", () => {
    if (kind === null || votingFor === null) return;
    overlay.remove();
    onDone({ kind, votingFor });
  });

  // Order: avatar → candidate images → phrase → enter. The selection area
  // scrolls if needed; the phrase + enter stay pinned (no scroll to reach them).
  const scroll = document.createElement("div");
  scroll.className =
    "flex flex-1 flex-col items-center gap-4 overflow-auto py-3";
  scroll.append(avatarHead, avatarRow, candRow);

  card.append(scroll, title, enter);
  overlay.appendChild(card);
  document.body.appendChild(overlay);
}
